// src/components/proposals/LeadSettingsPanel.jsx
//
// The settings pane for a Lead pre-proposal, shared by the Lead builder and the
// Lead template editor exactly as ProposalSettingsPanel is for Bronze/Gold.
// `hideCustomerFields` gates the per-customer controls, which a template does
// not own.
//
// This is structurally different from ProposalSettingsPanel, which edits ONE
// tier's narrative. A Lead's content is a MATRIX — 4 tiers × 20-odd features —
// so the centrepiece here is a grid of tick boxes rather than a column of
// textareas. That is why it is its own component and not a flag on the other.
//
// A feature row carries EITHER `tiers` (booleans) OR `textValues` (strings).
// Both the editor and the document branch on isTextRow(), and the ⇄ control on
// each row converts between them — without it a row added here could only ever
// be a tick row, and two of the seeded rows are not.
import React from "react";
import { Panel, LinkNote } from "./ProposalSettingsPanel";
import ClientTypeahead from "../common/ClientTypeahead";
import { readCoverImage } from "./proposalChrome";
import {
  LEAD_TIER_KEYS, LEAD_TIER_LABELS, LEAD_PAGE_KEYS, isTextRow,
  makeLeadFeature, makeLeadSection, makeLeadAddOn,
} from "./leadContent";

/* Page-visibility switch — teal when on, grey when off. Purely presentational;
   the row owns the click. Mirrors ProposalSettingsPanel's. */
function ToggleSwitch({ on, disabled }) {
  return (
    <div style={{ width: 36, height: 20, borderRadius: 10, position: "relative", flexShrink: 0,
                  background: on ? "#0b80a5" : "#374151", transition: "background .2s",
                  opacity: disabled ? 0.5 : 1 }}>
      <div style={{ position: "absolute", top: 3, left: on ? 19 : 3, width: 14, height: 14,
                    borderRadius: "50%", background: "#fff", transition: "left .2s" }} />
    </div>
  );
}

const iconBtn = {
  fontSize: 10, padding: "2px 7px", lineHeight: 1.4,
};

/* Rate fields are free text, not `type="number"`.

   Two reasons, both learned here already. A numeric input seeded with a literal
   0 appends rather than replaces — typing 49 gives `049` — which is the bug the
   Foundational line-item fields were rewritten to avoid. And a rate is not
   always a number: "POA" is a legitimate thing to put in that box, and a number
   input silently refuses it. LeadTemplate.rateText() prints a clean number with
   its unit and anything else verbatim.

   Empty commits null rather than "", so a tier with no per-user rate reads as
   device-priced instead of showing an empty price line. */
function RateField({ label, value, onChange, disabled, placeholder }) {
  return (
    <div style={{ flex: 1 }}>
      <label className="prop-lbl">{label}</label>
      <input className="inp" type="text" inputMode="decimal" disabled={disabled}
             style={{ marginBottom: 0, fontSize: 12 }}
             placeholder={placeholder}
             value={value === null || value === undefined ? "" : String(value)}
             onChange={(e) => {
               const raw = e.target.value.trim();
               onChange(raw === "" ? null : raw);
             }} />
    </div>
  );
}

export default function LeadSettingsPanel({ p, onContent, coverImageUrl, onCover, disabled,
                                            hideCustomerFields = false,
                                            openOpportunities = [], onLinkOpportunity,
                                            linkedOpportunity = null,
                                            atCompanies = [], onCompany }) {
  const c = p.content || {};
  const set = (patch) => { if (!disabled) onContent(patch); };

  /* The picker offers OPEN opportunities, plus the linked one when it has since
     been closed. Without that last entry the select would have a value matching
     no option, render as if nothing were linked, and the next change would
     silently unlink a deal instead of moving it. Same trap as the proposal
     panel — see ProposalSettingsPanel. */
  const opportunityOptions = linkedOpportunity
    && !openOpportunities.some((o) => o.id === linkedOpportunity.id)
    ? [...openOpportunities, { ...linkedOpportunity, closed: true }]
    : openOpportunities;

  return (
    <>
      <style>{`
        .prop-lbl{display:block;font-size:9px;font-weight:700;letter-spacing:.08em;
                  text-transform:uppercase;color:var(--text3);margin-bottom:3px;}
        .prop-row{display:flex;gap:8px;margin-bottom:8px;}
        .lead-mx{width:100%;border-collapse:collapse;}
        .lead-mx th{font-size:8px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;
                    color:var(--text3);padding:2px 0;text-align:center;}
        .lead-mx td{padding:3px 2px;vertical-align:top;}
      `}</style>

      {!hideCustomerFields && (
        <Panel title="Customer">
          {/* Identical to the proposal panel's, deliberately. A Lead left on the
              old plain input would keep the old behaviour silently, and the
              inconsistency would surface months later as "why did onboarding
              pre-fill for that one and not this one". See companyLink.js. */}
          <div style={{ marginBottom: 8 }}>
            <label className="prop-lbl">Company name</label>
            <ClientTypeahead
              value={c.customerName || ""}
              companies={atCompanies}
              placeholder="Company name — type a new one if they're not in Autotask"
              inputStyle={{ marginBottom: 0, fontSize: 12 }}
              onChange={(name, match) => {
                if (disabled) return;
                if (onCompany) onCompany(name, match);
                else set({ customerName: name });   // template editor: no record to link
              }} />
            <LinkNote linked={!!p.companyId} name={c.customerName} />
          </div>
          <div className="prop-row">
            <div style={{ flex: 1 }}>
              <label className="prop-lbl">Contact full name</label>
              <input className="inp" style={{ marginBottom: 0, fontSize: 12 }} disabled={disabled}
                     value={c.customerFullName || ""}
                     onChange={(e) => set({ customerFullName: e.target.value })} />
            </div>
            <div style={{ flex: 1 }}>
              <label className="prop-lbl">Date</label>
              <input className="inp" style={{ marginBottom: 0, fontSize: 12 }} disabled={disabled}
                     placeholder="August 2026" value={c.proposalDate || ""}
                     onChange={(e) => set({ proposalDate: e.target.value })} />
            </div>
          </div>
          <div className="prop-row" style={{ marginBottom: 0 }}>
            <div style={{ flex: 1 }}>
              <label className="prop-lbl">Sales rep</label>
              <input className="inp" style={{ marginBottom: 0, fontSize: 12 }} disabled={disabled}
                     value={c.salesRepName || ""}
                     onChange={(e) => set({ salesRepName: e.target.value })} />
            </div>
            <div style={{ flex: 1 }}>
              <label className="prop-lbl">Title</label>
              <input className="inp" style={{ marginBottom: 0, fontSize: 12 }} disabled={disabled}
                     value={c.salesRepTitle || ""}
                     onChange={(e) => set({ salesRepTitle: e.target.value })} />
            </div>
          </div>
        </Panel>
      )}

      {/* opportunityId lives on the RECORD, not inside content, so it goes
          through onLinkOpportunity rather than set(). A sent Lead is what turns
          the opportunity's card orange — an unsent draft deliberately does not,
          unlike a proposal or quote. See opportunityPipeline.js. */}
      {!hideCustomerFields && onLinkOpportunity && (
        <Panel title="Opportunity">
          <select className="inp" style={{ marginBottom: 0, fontSize: 12 }} disabled={disabled}
                  value={p.opportunityId || ""}
                  onChange={(e) => onLinkOpportunity(e.target.value || null)}>
            <option value="">— None —</option>
            {opportunityOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.title || "(untitled)"}{o.companyName ? ` — ${o.companyName}` : ""}
                {o.closed ? " (closed)" : ""}
              </option>
            ))}
          </select>
          <div style={{ fontSize: 9, color: "var(--text3)", marginTop: 6, lineHeight: 1.5 }}>
            {p.opportunityId
              ? "Linked. This Lead counts toward that opportunity's pipeline colour once it is marked sent — a draft does not."
              : "Optional. Linking, then marking the Lead sent, moves the opportunity's card from red to orange."}
          </div>
        </Panel>
      )}

      <Panel title="Introduction">
        <div style={{ marginBottom: 8 }}>
          <label className="prop-lbl">Heading</label>
          <input className="inp" type="text" style={{ marginBottom: 0, fontSize: 12 }}
                 disabled={disabled} value={c.heroHeading || ""}
                 onChange={(e) => set({ heroHeading: e.target.value })} />
        </div>
        <div>
          <label className="prop-lbl">Body</label>
          <textarea className="inp" rows={7} disabled={disabled}
                    style={{ marginBottom: 0, fontSize: 12, resize: "vertical" }}
                    value={c.heroBody || ""}
                    onChange={(e) => set({ heroBody: e.target.value })} />
        </div>
      </Panel>

      <TiersEditor c={c} set={set} disabled={disabled} />
      <FeatureMatrixEditor c={c} set={set} disabled={disabled} />
      <AddOnsEditor c={c} set={set} disabled={disabled} />
      <TermsEditor c={c} set={set} disabled={disabled} />
      <ResponseTimesEditor c={c} set={set} disabled={disabled} />

      {!hideCustomerFields && (
        <Panel title="Cover image">
          {coverImageUrl ? (
            <>
              <img src={coverImageUrl} alt="" style={{ width: "100%", height: 80, objectFit: "cover",
                                                       borderRadius: 6, display: "block", marginBottom: 8 }} />
              <button className="btn" style={{ fontSize: 10 }} disabled={disabled}
                      onClick={() => onCover(null)}>Remove</button>
            </>
          ) : (
            <label className="btn" style={{ fontSize: 10, display: "inline-block", cursor: "pointer" }}>
              Upload cover photo
              <input type="file" accept="image/*" style={{ display: "none" }} disabled={disabled}
                     onChange={(e) => readCoverImage(e, (dataUrl) => onCover(dataUrl))} />
            </label>
          )}
          <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 6 }}>
            JPG or PNG — auto-compressed to 1600px before saving.
          </div>
        </Panel>
      )}

      <Panel title="Page visibility">
        {LEAD_PAGE_KEYS.map(([key, label]) => (
          <div key={key}
               onClick={() => { if (!disabled) set({ [key]: c[key] === false }); }}
               style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "6px 8px", marginBottom: 4, borderRadius: 6,
                        background: "var(--s1)", border: "1px solid var(--border)",
                        cursor: disabled ? "default" : "pointer" }}>
            <span style={{ fontSize: 11, color: "var(--text2)" }}>{label}</span>
            <ToggleSwitch on={c[key] !== false} disabled={disabled} />
          </div>
        ))}
        <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 6 }}>
          The cover page is always included.
        </div>
      </Panel>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Service plans — blurb + rates per tier
   ══════════════════════════════════════════════════════════════════════ */
function TiersEditor({ c, set, disabled }) {
  const tiers = Array.isArray(c.tiers) ? c.tiers : [];
  const patch = (i, p) => set({ tiers: tiers.map((t, j) => (j === i ? { ...t, ...p } : t)) });

  return (
    <Panel title="Service plans">
      {tiers.map((tier, i) => (
        <div key={tier.key || i} style={{ background: "var(--s1)", border: "1px solid var(--border)",
                                          borderRadius: 6, padding: 8, marginBottom: 8 }}>
          <input className="inp" style={{ marginBottom: 6, fontSize: 12, fontWeight: 700 }}
                 disabled={disabled} placeholder="Plan name"
                 value={tier.name || ""} onChange={(e) => patch(i, { name: e.target.value })} />
          <div className="prop-row">
            <RateField label="$/device/mth" value={tier.perDeviceRate} disabled={disabled}
                       placeholder="49" onChange={(v) => patch(i, { perDeviceRate: v })} />
            <RateField label="$/user/mth" value={tier.perUserRate} disabled={disabled}
                       placeholder="blank = none"
                       onChange={(v) => patch(i, { perUserRate: v })} />
          </div>
          <textarea className="inp" rows={4} disabled={disabled}
                    style={{ marginBottom: 0, fontSize: 12, resize: "vertical" }}
                    placeholder="What this plan is for…"
                    value={tier.blurb || ""} onChange={(e) => patch(i, { blurb: e.target.value })} />
        </div>
      ))}
      <div style={{ fontSize: 10, color: "var(--text3)", lineHeight: 1.5 }}>
        Leave $/user blank for a device-priced plan — the document then reads
        "device-based pricing, no per-user fee" instead of an empty price.
      </div>
    </Panel>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Feature comparison matrix

   Craig's ask, verbatim: "a tick box I can add or remove from each plan".
   That is the four checkboxes per row. Rows and whole sections can be added
   and removed too, and ⇄ converts a row between tick boxes and free text for
   the rows that carry a phrase rather than a yes/no.
   ══════════════════════════════════════════════════════════════════════ */
function FeatureMatrixEditor({ c, set, disabled }) {
  const sections = Array.isArray(c.featureSections) ? c.featureSections : [];

  const patchSection = (sIdx, p) =>
    set({ featureSections: sections.map((s, j) => (j === sIdx ? { ...s, ...p } : s)) });

  const featuresOf = (s) => (Array.isArray(s?.features) ? s.features : []);

  const patchFeature = (sIdx, fIdx, p) => patchSection(sIdx, {
    features: featuresOf(sections[sIdx]).map((f, j) => (j === fIdx ? { ...f, ...p } : f)),
  });

  const removeFeature = (sIdx, fIdx) => patchSection(sIdx, {
    features: featuresOf(sections[sIdx]).filter((_, j) => j !== fIdx),
  });

  const addFeature = (sIdx) => patchSection(sIdx, {
    features: [...featuresOf(sections[sIdx]), makeLeadFeature()],
  });

  const removeSection = (sIdx) => {
    const s = sections[sIdx];
    const n = featuresOf(s).length;
    if (n && !window.confirm(
      `Delete the "${s.title || "untitled"}" section and its ${n} feature${n === 1 ? "" : "s"}?`
    )) return;
    set({ featureSections: sections.filter((_, j) => j !== sIdx) });
  };

  const addSection = () => set({ featureSections: [...sections, makeLeadSection()] });

  /* Converting between the two row shapes. The old values are DISCARDED rather
     than carried across — a tick has no sensible text and a phrase has no
     sensible boolean, and a silent guess ("✓" becomes "Yes") would put words on
     a customer document that nobody wrote. */
  const toggleRowKind = (sIdx, fIdx) => {
    const f = featuresOf(sections[sIdx])[fIdx];
    if (isTextRow(f)) {
      const tiers = {};
      LEAD_TIER_KEYS.forEach((k) => { tiers[k] = false; });
      patchFeature(sIdx, fIdx, { tiers, textValues: undefined });
    } else {
      const textValues = {};
      LEAD_TIER_KEYS.forEach((k) => { textValues[k] = ""; });
      patchFeature(sIdx, fIdx, { textValues, tiers: undefined });
    }
  };

  return (
    <Panel title="Feature comparison">
      {sections.map((section, sIdx) => (
        <div key={sIdx} style={{ marginBottom: 12, background: "var(--s1)",
                                 border: "1px solid var(--border)", borderRadius: 6, padding: 8 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
            <input className="inp" style={{ marginBottom: 0, fontSize: 12, fontWeight: 700 }}
                   disabled={disabled} placeholder="Section title"
                   value={section.title || ""}
                   onChange={(e) => patchSection(sIdx, { title: e.target.value })} />
            <button className="btn btn-danger" style={iconBtn} disabled={disabled}
                    title="Delete this section and every feature in it"
                    onClick={() => removeSection(sIdx)}>✕</button>
          </div>

          <table className="lead-mx">
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Feature</th>
                {LEAD_TIER_KEYS.map((key) => (
                  <th key={key} style={{ width: 34 }}>{LEAD_TIER_LABELS[key].slice(0, 3)}</th>
                ))}
                <th style={{ width: 26 }} />
              </tr>
            </thead>
            <tbody>
              {featuresOf(section).map((feature, fIdx) => {
                const text = isTextRow(feature);
                return (
                  <tr key={fIdx} style={{ borderTop: "1px solid var(--border)" }}>
                    <td>
                      <input className="inp" style={{ marginBottom: 3, fontSize: 11 }}
                             disabled={disabled} placeholder="Feature name"
                             value={feature.name || ""}
                             onChange={(e) => patchFeature(sIdx, fIdx, { name: e.target.value })} />
                      <textarea className="inp" rows={2} disabled={disabled}
                                style={{ marginBottom: 0, fontSize: 10.5, resize: "vertical" }}
                                placeholder="Description…"
                                value={feature.description || ""}
                                onChange={(e) => patchFeature(sIdx, fIdx, { description: e.target.value })} />
                      {text && (
                        <div style={{ display: "flex", gap: 3, marginTop: 3 }}>
                          {LEAD_TIER_KEYS.map((key) => (
                            <input key={key} className="inp" disabled={disabled}
                                   style={{ marginBottom: 0, fontSize: 10, flex: 1, minWidth: 0 }}
                                   title={LEAD_TIER_LABELS[key]}
                                   placeholder={LEAD_TIER_LABELS[key]}
                                   value={feature.textValues?.[key] || ""}
                                   onChange={(e) => patchFeature(sIdx, fIdx, {
                                     textValues: { ...feature.textValues, [key]: e.target.value },
                                   })} />
                          ))}
                        </div>
                      )}
                    </td>
                    {LEAD_TIER_KEYS.map((key) => (
                      <td key={key} style={{ textAlign: "center", verticalAlign: "top",
                                             paddingTop: 8 }}>
                        {text ? (
                          <span style={{ fontSize: 10, color: "var(--text3)" }}>—</span>
                        ) : (
                          <input type="checkbox" disabled={disabled}
                                 checked={!!feature.tiers?.[key]}
                                 onChange={(e) => patchFeature(sIdx, fIdx, {
                                   tiers: { ...feature.tiers, [key]: e.target.checked },
                                 })} />
                        )}
                      </td>
                    ))}
                    <td style={{ verticalAlign: "top", paddingTop: 4 }}>
                      <button className="btn" style={{ ...iconBtn, padding: "1px 4px" }}
                              disabled={disabled}
                              title={text ? "Switch back to tick boxes" : "Switch to free text per plan"}
                              onClick={() => toggleRowKind(sIdx, fIdx)}>⇄</button>
                      <button className="btn btn-danger"
                              style={{ ...iconBtn, padding: "1px 4px", marginTop: 3 }}
                              disabled={disabled} title="Remove this feature"
                              onClick={() => removeFeature(sIdx, fIdx)}>✕</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <button className="btn" style={{ fontSize: 10, marginTop: 6 }} disabled={disabled}
                  onClick={() => addFeature(sIdx)}>+ Add feature</button>
        </div>
      ))}
      <button className="btn btn-strat" style={{ fontSize: 10 }} disabled={disabled}
              onClick={addSection}>+ Add section</button>
      <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 6, lineHeight: 1.5 }}>
        ⇄ switches a row between tick boxes and a phrase per plan (used by
        Unlimited Remote Support and Technology Business Review). Switching
        clears that row's values.
      </div>
    </Panel>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Optional add-ons — every cell is free text, because the real values are a
   mix of "$50", "Included" and blank.
   ══════════════════════════════════════════════════════════════════════ */
function AddOnsEditor({ c, set, disabled }) {
  const addOns = Array.isArray(c.addOns) ? c.addOns : [];
  const patch = (i, p) => set({ addOns: addOns.map((a, j) => (j === i ? { ...a, ...p } : a)) });
  const remove = (i) => set({ addOns: addOns.filter((_, j) => j !== i) });
  const add = () => set({ addOns: [...addOns, makeLeadAddOn()] });

  return (
    <Panel title="Optional add-ons">
      {addOns.map((addOn, i) => (
        <div key={i} style={{ background: "var(--s1)", border: "1px solid var(--border)",
                              borderRadius: 6, padding: 8, marginBottom: 8 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 4 }}>
            <input className="inp" style={{ marginBottom: 0, fontSize: 12, fontWeight: 600 }}
                   disabled={disabled} placeholder="Add-on name"
                   value={addOn.name || ""} onChange={(e) => patch(i, { name: e.target.value })} />
            <button className="btn btn-danger" style={iconBtn} disabled={disabled}
                    onClick={() => remove(i)}>✕</button>
          </div>
          <input className="inp" style={{ marginBottom: 4, fontSize: 11 }} disabled={disabled}
                 placeholder="Description (optional)"
                 value={addOn.description || ""}
                 onChange={(e) => patch(i, { description: e.target.value })} />
          <input className="inp" style={{ marginBottom: 6, fontSize: 11 }} disabled={disabled}
                 placeholder="Unit — e.g. per user, per email address"
                 value={addOn.unit || ""} onChange={(e) => patch(i, { unit: e.target.value })} />
          <div style={{ display: "flex", gap: 4 }}>
            {LEAD_TIER_KEYS.map((key) => (
              <div key={key} style={{ flex: 1, minWidth: 0 }}>
                <label className="prop-lbl">{LEAD_TIER_LABELS[key]}</label>
                <input className="inp" style={{ marginBottom: 0, fontSize: 11 }} disabled={disabled}
                       placeholder="$0"
                       value={addOn.pricing?.[key] || ""}
                       onChange={(e) => patch(i, {
                         pricing: { ...addOn.pricing, [key]: e.target.value },
                       })} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <button className="btn" style={{ fontSize: 10, marginBottom: 8 }} disabled={disabled}
              onClick={add}>+ Add add-on</button>
      <label className="prop-lbl">Closing note</label>
      <textarea className="inp" rows={3} disabled={disabled}
                style={{ marginBottom: 0, fontSize: 11, resize: "vertical" }}
                value={c.addOnsNote || ""} onChange={(e) => set({ addOnsNote: e.target.value })} />
      <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 6, lineHeight: 1.5 }}>
        Prices are free text so "Included" and a blank cell are both valid.
        A blank cell prints as a dash.
      </div>
    </Panel>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Terms & conditions
   ══════════════════════════════════════════════════════════════════════ */
function TermsEditor({ c, set, disabled }) {
  const terms = Array.isArray(c.termsAndConditions) ? c.termsAndConditions : [];
  const write = (next) => set({ termsAndConditions: next });
  const patch = (i, v) => write(terms.map((t, j) => (j === i ? v : t)));
  const remove = (i) => write(terms.filter((_, j) => j !== i));
  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= terms.length) return;
    const next = [...terms];
    [next[i], next[j]] = [next[j], next[i]];
    write(next);
  };

  return (
    <Panel title="Terms & conditions">
      {terms.map((term, i) => (
        <div key={i} style={{ background: "var(--s1)", border: "1px solid var(--border)",
                              borderRadius: 6, padding: 8, marginBottom: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                        marginBottom: 5 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: "var(--strat)",
                           letterSpacing: "0.1em", textTransform: "uppercase" }}>Term {i + 1}</span>
            <div style={{ display: "flex", gap: 4 }}>
              <button className="btn" style={iconBtn} disabled={disabled || i === 0}
                      title="Move up" onClick={() => move(i, -1)}>↑</button>
              <button className="btn" style={iconBtn} disabled={disabled || i === terms.length - 1}
                      title="Move down" onClick={() => move(i, 1)}>↓</button>
              <button className="btn btn-danger" style={iconBtn} disabled={disabled}
                      onClick={() => remove(i)}>✕</button>
            </div>
          </div>
          <textarea className="inp" rows={4} disabled={disabled}
                    style={{ marginBottom: 0, fontSize: 11, resize: "vertical" }}
                    value={term || ""} onChange={(e) => patch(i, e.target.value)} />
        </div>
      ))}
      <button className="btn" style={{ fontSize: 10 }} disabled={disabled}
              onClick={() => write([...terms, ""])}>+ Add term</button>
    </Panel>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Response times — a fixed five-priority table, every cell editable
   ══════════════════════════════════════════════════════════════════════ */
function ResponseTimesEditor({ c, set, disabled }) {
  const rows = Array.isArray(c.responseTimes) ? c.responseTimes : [];
  const patch = (i, p) => set({ responseTimes: rows.map((r, j) => (j === i ? { ...r, ...p } : r)) });

  const patchExample = (i, k, v) => patch(i, {
    examples: (rows[i].examples || []).map((e, j) => (j === k ? v : e)),
  });
  const removeExample = (i, k) => patch(i, {
    examples: (rows[i].examples || []).filter((_, j) => j !== k),
  });
  const addExample = (i) => patch(i, { examples: [...(rows[i].examples || []), ""] });

  return (
    <Panel title="Response times">
      {rows.map((row, i) => (
        <div key={row.priority || i} style={{ background: "var(--s1)", border: "1px solid var(--border)",
                                              borderRadius: 6, padding: 8, marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                           background: row.dot || "#9ca3af" }} />
            <input className="inp" style={{ marginBottom: 0, fontSize: 12, fontWeight: 600 }}
                   disabled={disabled} placeholder="Priority"
                   value={row.priority || ""}
                   onChange={(e) => patch(i, { priority: e.target.value })} />
          </div>
          <div className="prop-row">
            <div style={{ flex: 1 }}>
              <label className="prop-lbl">Copper &amp; Bronze</label>
              <input className="inp" style={{ marginBottom: 0, fontSize: 11 }} disabled={disabled}
                     value={row.copperBronze || ""}
                     onChange={(e) => patch(i, { copperBronze: e.target.value })} />
            </div>
            <div style={{ flex: 1 }}>
              <label className="prop-lbl">Silver</label>
              <input className="inp" style={{ marginBottom: 0, fontSize: 11 }} disabled={disabled}
                     value={row.silver || ""}
                     onChange={(e) => patch(i, { silver: e.target.value })} />
            </div>
            <div style={{ flex: 1 }}>
              <label className="prop-lbl">Gold</label>
              <input className="inp" style={{ marginBottom: 0, fontSize: 11 }} disabled={disabled}
                     value={row.gold || ""}
                     onChange={(e) => patch(i, { gold: e.target.value })} />
            </div>
          </div>
          <label className="prop-lbl">Examples</label>
          {(row.examples || []).map((ex, k) => (
            <div key={k} style={{ display: "flex", gap: 4, marginBottom: 4 }}>
              <input className="inp" style={{ marginBottom: 0, fontSize: 11 }} disabled={disabled}
                     value={ex || ""} onChange={(e) => patchExample(i, k, e.target.value)} />
              <button className="btn btn-danger" style={iconBtn} disabled={disabled}
                      onClick={() => removeExample(i, k)}>✕</button>
            </div>
          ))}
          <button className="btn" style={{ fontSize: 10 }} disabled={disabled}
                  onClick={() => addExample(i)}>+ Add example</button>
        </div>
      ))}
      <div style={{ fontSize: 10, color: "var(--text3)", lineHeight: 1.5 }}>
        "N/A" renders greyed out; anything else renders as a committed response time.
      </div>
    </Panel>
  );
}
